import * as types from '../mutation-types';
import SalesFunnelAPI from '../../api/salesFunnel';

// Epoch ms from an ISO string or epoch-seconds number (websocket convention).
const toEpoch = value => {
  if (!value) return 0;
  if (typeof value === 'number') return value * 1000;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

// "Needs attention" order: unread first, then most recent activity, then newest.
const compareByAttention = (a, b) => {
  const unread =
    (b.conversation?.unread_count || 0) - (a.conversation?.unread_count || 0);
  if (unread !== 0) return unread;
  const activity =
    toEpoch(b.conversation?.last_activity_at) -
    toEpoch(a.conversation?.last_activity_at);
  if (activity !== 0) return activity;
  return toEpoch(b.created_at) - toEpoch(a.created_at);
};

export const state = {
  funnels: [],
  currentFunnelId: null,
  stages: [],
  deals: [],
  // Per-stage state, keyed by stageId:
  // { deals: [], totalCount, totalValue, isLoading }
  stageData: {},
  // Current board sort + filters + page size + active view, so store-initiated
  // refreshes (e.g. after a bulk update) can refetch just the visible view.
  boardParams: {
    sort: undefined,
    filters: {},
    perPage: undefined,
    view: 'kanban',
  },
  // Flat table view state (deals live in `deals`).
  tableMeta: {
    totalCount: 0,
    totalValue: 0,
    activeCount: 0,
    activeValue: 0,
    isLoading: false,
  },
  activities: [],
  reports: {},
  selectedDeal: null,
  selectedDealIds: [],
  labels: [],
  tasks: [],
  lostReasons: [],
  uiFlags: {
    isFetchingStages: false,
    isFetchingDeals: false,
    isFetchingActivities: false,
    isFetchingReports: false,
    isFetchingTasks: false,
    isCreating: false,
    isUpdating: false,
    isMoving: false,
  },
};

export const getters = {
  getFunnels: $state => $state.funnels,
  getCurrentFunnelId: $state => $state.currentFunnelId,
  getStages: $state => $state.stages,
  getDeals: $state => $state.deals,
  getSelectedDealIds: $state => $state.selectedDealIds,
  getDealsByStage: $state => stageId =>
    $state.deals.filter(deal => deal.funnel_stage_id === stageId),
  // Per-stage getters (used by the kanban columns)
  getStageDeals: $state => stageId => $state.stageData[stageId]?.deals || [],
  getStageIsLoading: $state => stageId =>
    $state.stageData[stageId]?.isLoading || false,
  getStageTotalCount: $state => stageId =>
    $state.stageData[stageId]?.totalCount || 0,
  getStageTotalValue: $state => stageId =>
    $state.stageData[stageId]?.totalValue || 0,
  // Table getters
  getTableMeta: $state => $state.tableMeta,
  getTableIsLoading: $state => $state.tableMeta.isLoading,
  // Union of flat deals (table view) and all loaded per-stage deals (kanban),
  // deduped by id. Handlers that need any loaded deal read from here.
  getAllLoadedDeals: $state => {
    const seen = new Set();
    const result = [];
    const push = deal => {
      if (!seen.has(deal.id)) {
        seen.add(deal.id);
        result.push(deal);
      }
    };
    $state.deals.forEach(push);
    Object.values($state.stageData).forEach(sd =>
      (sd.deals || []).forEach(push)
    );
    return result;
  },
  getDealById: $state => dealId =>
    $state.deals.find(deal => deal.id === dealId) ||
    Object.values($state.stageData)
      .flatMap(sd => sd.deals || [])
      .find(deal => deal.id === dealId),
  getActivities: $state => $state.activities,
  getTasks: $state => $state.tasks,
  getReports: $state => $state.reports,
  getSelectedDeal: $state => $state.selectedDeal,
  getUIFlags: $state => $state.uiFlags,
  getLabels: $state => $state.labels,
  getLostReasons: $state => $state.lostReasons,
  getTotalPipelineValue: $state =>
    $state.deals
      .filter(deal => {
        const stage = $state.stages.find(s => s.id === deal.funnel_stage_id);
        return stage && stage.stage_type === 'open';
      })
      .reduce((sum, deal) => sum + (deal.value || 0), 0),
  getActiveDealsCount: $state =>
    $state.deals.filter(deal => {
      const stage = $state.stages.find(s => s.id === deal.funnel_stage_id);
      return stage && stage.stage_type === 'open';
    }).length,
};

export const actions = {
  // Funnels (Pipelines)
  fetchFunnels: async ({ commit, state: $state }) => {
    const response = await SalesFunnelAPI.getFunnels();
    commit(types.default.SET_FUNNELS, response.data);
    if (response.data.length && !$state.currentFunnelId) {
      commit(types.default.SET_CURRENT_FUNNEL_ID, response.data[0].id);
    }
    return response.data;
  },

  createFunnel: async ({ commit }, funnelData) => {
    const response = await SalesFunnelAPI.createFunnel(funnelData);
    commit(types.default.ADD_FUNNEL, response.data);
    return response.data;
  },

  updateFunnel: async ({ commit }, { id, ...funnelData }) => {
    const response = await SalesFunnelAPI.updateFunnel(id, funnelData);
    commit(types.default.EDIT_FUNNEL, response.data);
    return response.data;
  },

  setCurrentFunnelId: ({ commit }, funnelId) => {
    commit(types.default.SET_CURRENT_FUNNEL_ID, funnelId);
  },

  // Stages
  fetchStages: async ({ commit, state: $state }) => {
    commit(types.default.SET_SALES_FUNNEL_UI_FLAG, {
      isFetchingStages: true,
    });
    try {
      const response = await SalesFunnelAPI.getStages($state.currentFunnelId);
      commit(types.default.SET_FUNNEL_STAGES, response.data);
    } catch (error) {
      // handle silently
    } finally {
      commit(types.default.SET_SALES_FUNNEL_UI_FLAG, {
        isFetchingStages: false,
      });
    }
  },

  createStage: async ({ commit }, stageData) => {
    const response = await SalesFunnelAPI.createStage(stageData);
    commit(types.default.ADD_FUNNEL_STAGE, response.data);
    return response.data;
  },

  updateStage: async ({ commit }, { id, ...stageData }) => {
    const response = await SalesFunnelAPI.updateStage(id, stageData);
    commit(types.default.EDIT_FUNNEL_STAGE, response.data);
    return response.data;
  },

  deleteStage: async ({ commit }, stageId) => {
    await SalesFunnelAPI.deleteStage(stageId);
    commit(types.default.DELETE_FUNNEL_STAGE, stageId);
  },

  reorderStages: async (context, stages) => {
    await SalesFunnelAPI.reorderStages(context.state.currentFunnelId, stages);
    const updatedStages = context.state.stages.map(existingStage => {
      const match = stages.find(s => s.id === existingStage.id);
      if (match) {
        return { ...existingStage, position: match.position };
      }
      return existingStage;
    });
    context.commit(types.default.SET_FUNNEL_STAGES, updatedStages);
  },

  // Deals
  // Per-stage fetch. The "Mostrar N" selector controls how many load; each
  // fetch replaces the stage's deals. Shared filters/sort/perPage passed in.
  fetchStageDeals: async (
    { commit, state: $state },
    { stageId, sort, filters = {}, perPage }
  ) => {
    commit(types.default.SET_STAGE_LOADING, { stageId, isLoading: true });
    try {
      const queryParams = { ...filters, funnel_stage_id: stageId, page: 1 };
      if ($state.currentFunnelId)
        queryParams.funnel_id = $state.currentFunnelId;
      if (sort) queryParams.sort = sort;
      if (perPage) queryParams.per_page = perPage;

      const response = await SalesFunnelAPI.getDeals(queryParams);
      const { deals, meta } = response.data.payload;

      commit(types.default.SET_STAGE_DEALS, {
        stageId,
        deals,
        totalCount: meta?.total_count || 0,
        totalValue: meta?.total_value || 0,
      });
      return response.data.payload;
    } catch (error) {
      commit(types.default.SET_STAGE_LOADING, { stageId, isLoading: false });
      throw error;
    }
  },

  setBoardParams: ({ commit }, params) => {
    commit(types.default.SET_BOARD_PARAMS, params);
  },

  // Refetch every stage using the stored board params. Used after writes
  // (bulk updates, etc.) so the kanban reflects changes.
  refreshStages: ({ dispatch, state: $state }) => {
    $state.stages.forEach(stage =>
      dispatch('fetchStageDeals', {
        stageId: stage.id,
        sort: $state.boardParams.sort,
        filters: $state.boardParams.filters,
        perPage: $state.boardParams.perPage,
      })
    );
  },

  // Flat fetch for the table view (no funnel_stage_id → all stages).
  fetchTableDeals: async (
    { commit, state: $state },
    { sort, filters = {}, perPage }
  ) => {
    commit(types.default.SET_TABLE_META, { isLoading: true });
    try {
      const queryParams = { ...filters, page: 1 };
      if ($state.currentFunnelId)
        queryParams.funnel_id = $state.currentFunnelId;
      if (sort) queryParams.sort = sort;
      if (perPage) queryParams.per_page = perPage;

      const response = await SalesFunnelAPI.getDeals(queryParams);
      const { deals, meta } = response.data.payload;

      commit(types.default.SET_DEALS, deals);
      commit(types.default.SET_TABLE_META, {
        totalCount: meta?.total_count || 0,
        totalValue: meta?.total_value || 0,
        activeCount: meta?.active_count || 0,
        activeValue: meta?.active_value || 0,
        isLoading: false,
      });
      return response.data.payload;
    } catch (error) {
      commit(types.default.SET_TABLE_META, { isLoading: false });
      throw error;
    }
  },

  refreshTable: ({ dispatch, state: $state }) =>
    dispatch('fetchTableDeals', {
      sort: $state.boardParams.sort,
      filters: $state.boardParams.filters,
      perPage: $state.boardParams.perPage,
    }),

  // Refresh only the currently visible view (used after bulk updates).
  refreshCurrentView: ({ dispatch, state: $state }) =>
    dispatch(
      $state.boardParams.view === 'table' ? 'refreshTable' : 'refreshStages'
    ),

  createDeal: async ({ commit }, dealData) => {
    commit(types.default.SET_SALES_FUNNEL_UI_FLAG, { isCreating: true });
    try {
      const response = await SalesFunnelAPI.createDeal(dealData);
      commit(types.default.ADD_DEAL, response.data);
      return response.data;
    } finally {
      commit(types.default.SET_SALES_FUNNEL_UI_FLAG, { isCreating: false });
    }
  },

  updateDeal: async ({ commit }, { id, ...dealData }) => {
    commit(types.default.SET_SALES_FUNNEL_UI_FLAG, { isUpdating: true });
    try {
      const response = await SalesFunnelAPI.updateDeal(id, dealData);
      commit(types.default.EDIT_DEAL, response.data);
      return response.data;
    } finally {
      commit(types.default.SET_SALES_FUNNEL_UI_FLAG, { isUpdating: false });
    }
  },

  deleteDeal: async ({ commit }, dealId) => {
    await SalesFunnelAPI.deleteDeal(dealId);
    commit(types.default.DELETE_DEAL, dealId);
  },

  moveDeal: async ({ commit }, { dealId, funnelStageId, password }) => {
    commit(types.default.SET_SALES_FUNNEL_UI_FLAG, { isMoving: true });
    try {
      const response = await SalesFunnelAPI.moveDeal(
        dealId,
        funnelStageId,
        password
      );
      commit(types.default.EDIT_DEAL, response.data);
      return response.data;
    } finally {
      commit(types.default.SET_SALES_FUNNEL_UI_FLAG, { isMoving: false });
    }
  },

  // Activities
  fetchActivities: async ({ commit }, dealId) => {
    commit(types.default.SET_SALES_FUNNEL_UI_FLAG, {
      isFetchingActivities: true,
    });
    try {
      const response = await SalesFunnelAPI.getDealActivities(dealId);
      commit(types.default.SET_DEAL_ACTIVITIES, response.data);
    } catch (error) {
      // handle silently
    } finally {
      commit(types.default.SET_SALES_FUNNEL_UI_FLAG, {
        isFetchingActivities: false,
      });
    }
  },

  // Reports
  fetchReports: async ({ commit, state: $state }, params = {}) => {
    commit(types.default.SET_SALES_FUNNEL_UI_FLAG, {
      isFetchingReports: true,
    });
    try {
      const queryParams = { ...params };
      if ($state.currentFunnelId) {
        queryParams.funnel_id = $state.currentFunnelId;
      }
      const response = await SalesFunnelAPI.getReports(queryParams);
      commit(types.default.SET_SALES_FUNNEL_REPORTS, response.data);
    } catch (error) {
      // handle silently
    } finally {
      commit(types.default.SET_SALES_FUNNEL_UI_FLAG, {
        isFetchingReports: false,
      });
    }
  },

  setSelectedDeal: ({ commit }, deal) => {
    commit(types.default.SET_SELECTED_DEAL, deal);
  },

  // Real-time conversation updates from WebSocket
  updateDealConversation: (
    { commit, getters: $getters },
    { conversationDisplayId, contactId, changes }
  ) => {
    const allDeals = $getters.getAllLoadedDeals;
    let deal;
    if (conversationDisplayId) {
      deal = allDeals.find(
        d => d.conversation?.display_id === conversationDisplayId
      );
    } else if (contactId) {
      deal = allDeals.find(d => d.contact?.id === contactId);
    }
    if (deal) {
      commit(types.default.UPDATE_DEAL_CONVERSATION, {
        dealId: deal.id,
        changes,
      });
    }
  },

  // Deal Labels
  fetchLabels: async ({ commit }) => {
    try {
      const response = await SalesFunnelAPI.getDealLabels();
      commit(types.default.SET_DEAL_LABELS, response.data);
      return response.data;
    } catch (error) {
      // handle silently
    }
    return [];
  },

  createLabel: async ({ commit }, labelData) => {
    const response = await SalesFunnelAPI.createDealLabel(labelData);
    commit(types.default.ADD_DEAL_LABEL, response.data);
    return response.data;
  },

  updateLabel: async ({ commit }, { labelId, labelData }) => {
    const response = await SalesFunnelAPI.updateDealLabel(labelId, labelData);
    commit(types.default.UPDATE_DEAL_LABEL, response.data);
    return response.data;
  },

  deleteLabel: async ({ commit }, labelId) => {
    await SalesFunnelAPI.deleteDealLabel(labelId);
    commit(types.default.DELETE_DEAL_LABEL, labelId);
  },

  // Deal Tasks
  fetchTasks: async ({ commit }, dealId) => {
    commit(types.default.SET_SALES_FUNNEL_UI_FLAG, {
      isFetchingTasks: true,
    });
    try {
      const response = await SalesFunnelAPI.getTasks(dealId);
      commit(types.default.SET_DEAL_TASKS, response.data);
      return response.data;
    } catch (error) {
      // handle silently
    } finally {
      commit(types.default.SET_SALES_FUNNEL_UI_FLAG, {
        isFetchingTasks: false,
      });
    }
    return [];
  },

  createTask: async ({ commit }, { dealId, task }) => {
    const response = await SalesFunnelAPI.createTask(dealId, task);
    commit(types.default.ADD_DEAL_TASK, response.data);
    return response.data;
  },

  updateTask: async ({ commit }, { dealId, taskId, task }) => {
    const response = await SalesFunnelAPI.updateTask(dealId, taskId, task);
    commit(types.default.EDIT_DEAL_TASK, response.data);
    return response.data;
  },

  deleteTask: async ({ commit }, { dealId, taskId }) => {
    await SalesFunnelAPI.deleteTask(dealId, taskId);
    commit(types.default.DELETE_DEAL_TASK, taskId);
  },

  setSelectedDealIds: ({ commit }, ids) => {
    commit(types.default.SET_SELECTED_DEAL_IDS, ids);
  },

  clearSelectedDealIds: ({ commit }) => {
    commit(types.default.CLEAR_SELECTED_DEAL_IDS);
  },

  toggleDealSelection: ({ commit, state: $state }, dealId) => {
    const ids = [...$state.selectedDealIds];
    const index = ids.indexOf(dealId);
    if (index > -1) {
      ids.splice(index, 1);
    } else {
      ids.push(dealId);
    }
    commit(types.default.SET_SELECTED_DEAL_IDS, ids);
  },

  bulkUpdateDeals: async ({ commit, dispatch }, payload) => {
    commit(types.default.SET_SALES_FUNNEL_UI_FLAG, { isUpdating: true });
    try {
      await SalesFunnelAPI.bulkUpdateDeals(payload);
      dispatch('refreshCurrentView');
      dispatch('clearSelectedDealIds');
    } finally {
      commit(types.default.SET_SALES_FUNNEL_UI_FLAG, { isUpdating: false });
    }
  },

  fetchLostReasons: async ({ commit }) => {
    try {
      const response = await SalesFunnelAPI.getLostReasons();
      commit(types.default.SET_DEAL_LOST_REASONS, response.data);
      return response.data;
    } catch (error) {
      // handle silently
    }
    return [];
  },

  createLostReason: async ({ commit }, data) => {
    const response = await SalesFunnelAPI.createLostReason(data);
    commit(types.default.ADD_DEAL_LOST_REASON, response.data);
    return response.data;
  },

  updateLostReason: async ({ commit }, { id, ...data }) => {
    const response = await SalesFunnelAPI.updateLostReason(id, data);
    commit(types.default.EDIT_DEAL_LOST_REASON, response.data);
    return response.data;
  },

  deleteLostReason: async ({ commit }, reasonId) => {
    await SalesFunnelAPI.deleteLostReason(reasonId);
    commit(types.default.DELETE_DEAL_LOST_REASON, reasonId);
  },

  createNote: async ({ dispatch }, { dealId, note }) => {
    const response = await SalesFunnelAPI.createDealActivity(dealId, { note });
    dispatch('fetchActivities', dealId);
    return response.data;
  },
};

export const mutations = {
  [types.default.SET_FUNNELS]($state, funnels) {
    $state.funnels = funnels;
  },
  [types.default.ADD_FUNNEL]($state, funnel) {
    $state.funnels.push(funnel);
  },
  [types.default.EDIT_FUNNEL]($state, updatedFunnel) {
    const index = $state.funnels.findIndex(f => f.id === updatedFunnel.id);
    if (index !== -1) {
      $state.funnels.splice(index, 1, updatedFunnel);
    }
  },
  [types.default.SET_CURRENT_FUNNEL_ID]($state, funnelId) {
    $state.currentFunnelId = funnelId;
  },
  [types.default.SET_SALES_FUNNEL_UI_FLAG]($state, uiFlags) {
    $state.uiFlags = { ...$state.uiFlags, ...uiFlags };
  },

  [types.default.SET_FUNNEL_STAGES]($state, stages) {
    $state.stages = stages;
  },
  [types.default.ADD_FUNNEL_STAGE]($state, stage) {
    $state.stages.push(stage);
    $state.stages.sort((a, b) => a.position - b.position);
  },
  [types.default.EDIT_FUNNEL_STAGE]($state, updatedStage) {
    const index = $state.stages.findIndex(s => s.id === updatedStage.id);
    if (index !== -1) {
      $state.stages.splice(index, 1, updatedStage);
    }
  },
  [types.default.DELETE_FUNNEL_STAGE]($state, stageId) {
    $state.stages = $state.stages.filter(s => s.id !== stageId);
  },

  [types.default.SET_DEALS]($state, deals) {
    $state.deals =
      $state.boardParams.sort === 'attention'
        ? [...deals].sort(compareByAttention)
        : deals;
    if ($state.selectedDeal) {
      const freshSelectedDeal = deals.find(
        d => d.id === $state.selectedDeal.id
      );
      if (freshSelectedDeal) {
        $state.selectedDeal = freshSelectedDeal;
      }
    }
  },
  [types.default.SET_TABLE_META]($state, meta) {
    $state.tableMeta = { ...$state.tableMeta, ...meta };
  },
  [types.default.ADD_DEAL]($state, deal) {
    $state.deals.push(deal);
  },
  [types.default.EDIT_DEAL]($state, updatedDeal) {
    const index = $state.deals.findIndex(d => d.id === updatedDeal.id);
    if (index !== -1) {
      $state.deals.splice(index, 1, updatedDeal);
    }
    if ($state.selectedDeal?.id === updatedDeal.id) {
      $state.selectedDeal = updatedDeal;
    }
  },
  [types.default.DELETE_DEAL]($state, dealId) {
    $state.deals = $state.deals.filter(d => d.id !== dealId);
  },

  [types.default.SET_STAGE_DEALS](
    $state,
    { stageId, deals, totalCount, totalValue }
  ) {
    const ordered =
      $state.boardParams.sort === 'attention'
        ? [...deals].sort(compareByAttention)
        : deals;
    $state.stageData = {
      ...$state.stageData,
      [stageId]: { deals: ordered, totalCount, totalValue, isLoading: false },
    };
    // Keep the open detail panel in sync with the freshly loaded deal.
    if ($state.selectedDeal) {
      const freshSelectedDeal = ordered.find(
        d => d.id === $state.selectedDeal.id
      );
      if (freshSelectedDeal) {
        $state.selectedDeal = freshSelectedDeal;
      }
    }
  },
  [types.default.SET_STAGE_LOADING]($state, { stageId, isLoading }) {
    const current = $state.stageData[stageId] || {};
    $state.stageData = {
      ...$state.stageData,
      [stageId]: { ...current, isLoading },
    };
  },
  [types.default.SET_BOARD_PARAMS]($state, params) {
    $state.boardParams = params;
  },

  [types.default.SET_DEAL_ACTIVITIES]($state, activities) {
    $state.activities = activities;
  },

  [types.default.SET_SALES_FUNNEL_REPORTS]($state, reports) {
    $state.reports = reports;
  },

  [types.default.SET_SELECTED_DEAL]($state, deal) {
    $state.selectedDeal = deal;
  },

  [types.default.UPDATE_DEAL_CONVERSATION]($state, { dealId, changes }) {
    const byAttention = $state.boardParams.sort === 'attention';

    // Flat deals (table view): update the conversation and re-sort by attention.
    const flatIdx = $state.deals.findIndex(d => d.id === dealId);
    if (flatIdx !== -1 && $state.deals[flatIdx].conversation) {
      const updated = [...$state.deals];
      updated[flatIdx] = {
        ...updated[flatIdx],
        conversation: { ...updated[flatIdx].conversation, ...changes },
      };
      $state.deals = byAttention ? updated.sort(compareByAttention) : updated;
    }

    // Per-stage deals (kanban): update the deal's conversation and, when the
    // board is sorted by attention, re-sort the loaded deals so the card
    // bubbles up live as messages arrive.
    Object.keys($state.stageData).forEach(stageId => {
      const sd = $state.stageData[stageId];
      const idx = (sd.deals || []).findIndex(d => d.id === dealId);
      if (idx === -1 || !sd.deals[idx].conversation) return;

      const updatedDeals = [...sd.deals];
      updatedDeals[idx] = {
        ...updatedDeals[idx],
        conversation: { ...updatedDeals[idx].conversation, ...changes },
      };
      if (byAttention) updatedDeals.sort(compareByAttention);
      $state.stageData = {
        ...$state.stageData,
        [stageId]: { ...sd, deals: updatedDeals },
      };
    });

    // Also update selectedDeal if it matches
    if (
      $state.selectedDeal?.id === dealId &&
      $state.selectedDeal?.conversation
    ) {
      $state.selectedDeal = {
        ...$state.selectedDeal,
        conversation: { ...$state.selectedDeal.conversation, ...changes },
      };
    }
  },

  [types.default.SET_DEAL_LABELS]($state, labels) {
    $state.labels = labels;
  },
  [types.default.ADD_DEAL_LABEL]($state, label) {
    $state.labels.push(label);
  },
  [types.default.UPDATE_DEAL_LABEL]($state, updatedLabel) {
    const index = $state.labels.findIndex(l => l.id === updatedLabel.id);
    if (index !== -1) {
      $state.labels.splice(index, 1, updatedLabel);
    }
  },
  [types.default.DELETE_DEAL_LABEL]($state, labelId) {
    $state.labels = $state.labels.filter(l => l.id !== labelId);
  },
  [types.default.SET_DEAL_TASKS]($state, tasks) {
    $state.tasks = tasks;
  },
  [types.default.ADD_DEAL_TASK]($state, task) {
    $state.tasks.push(task);
  },
  [types.default.EDIT_DEAL_TASK]($state, updatedTask) {
    const index = $state.tasks.findIndex(t => t.id === updatedTask.id);
    if (index !== -1) {
      $state.tasks.splice(index, 1, updatedTask);
    }
  },
  [types.default.DELETE_DEAL_TASK]($state, taskId) {
    $state.tasks = $state.tasks.filter(t => t.id !== taskId);
  },
  [types.default.SET_SELECTED_DEAL_IDS]($state, ids) {
    $state.selectedDealIds = ids;
  },
  [types.default.CLEAR_SELECTED_DEAL_IDS]($state) {
    $state.selectedDealIds = [];
  },
  [types.default.SET_DEAL_LOST_REASONS]($state, lostReasons) {
    $state.lostReasons = lostReasons;
  },
  [types.default.ADD_DEAL_LOST_REASON]($state, reason) {
    $state.lostReasons.push(reason);
  },
  [types.default.EDIT_DEAL_LOST_REASON]($state, updatedReason) {
    const index = $state.lostReasons.findIndex(r => r.id === updatedReason.id);
    if (index !== -1) {
      $state.lostReasons.splice(index, 1, updatedReason);
    }
  },
  [types.default.DELETE_DEAL_LOST_REASON]($state, reasonId) {
    $state.lostReasons = $state.lostReasons.filter(r => r.id !== reasonId);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
